import { Button } from "@screener/uikit/components/button";
import { Field, FieldError, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@screener/uikit/components/popover";
import { useState } from "react";
import {
  DISPLAY_MIN_AGE_BAD_NUMBER,
  DISPLAY_MIN_AGE_EXPLANATION,
  DISPLAY_MIN_AGE_FIELD_LABEL,
  DISPLAY_MIN_AGE_LOADING,
  DISPLAY_MIN_AGE_REJECTED,
  DISPLAY_MIN_AGE_REMOVE_FAILED,
  DISPLAY_MIN_AGE_SAVE_FAILED,
  DISPLAY_MIN_AGE_TITLE,
  DISPLAY_MIN_AGE_UNAVAILABLE_TEXT,
  DISPLAY_MINIMUMS_REMOVE,
  DISPLAY_MINIMUMS_RETRY,
  DISPLAY_MINIMUMS_SAVE,
  displayMinAgeButtonParts,
} from "@/lib/labels";
import { type DisplayMinAgeState, displayMinAgeUnavailable } from "./display-min-age";
import { ChipContent, type ChipTone, chipToneClass, chipVariant } from "./filter-chip";
import type { DisplayMinAgeOutcome } from "./use-display-min-age";

/**
 * Кнопка времени до показа §82 в полосе фильтров и её окно (design.md
 * stage9-display-min-age, Д6). Часть экрана, не UIKit: знает про настройку
 * плотностей. Правит ту же настройку, что читает отбор строк, теми же `save` и
 * `remove` из одного экземпляра `useDisplayMinAge`.
 */

/** Верхняя граница, которую демон принимает (`i32::MAX`, design.md Д2). */
const MAX_MIN_AGE_SEC = 2_147_483_647;

/** Разбор поля секунд: только целое не меньше нуля, без знака, дроби и букв. */
export function parseDisplayMinAgeInput(
  draft: string,
): { ok: true; value: number } | { ok: false } {
  const text = draft.trim();
  if (!/^\d+$/.test(text)) {
    return { ok: false };
  }
  const value = Number(text);
  return value <= MAX_MIN_AGE_SEC ? { ok: true, value } : { ok: false };
}

export type DisplayMinAgeActionResult = { status: "ok" } | { status: "error"; text: string };

function failureText(outcome: Exclude<DisplayMinAgeOutcome, "ok">, fallback: string): string {
  return outcome === "invalid" ? DISPLAY_MIN_AGE_REJECTED : fallback;
}

/**
 * «Сохранить»: нечитаемый ввод не отправляется, неудача возвращает текст
 * ошибки — окно остаётся открытым с введённым числом.
 */
export async function runDisplayMinAgeSave(
  draft: string,
  onSave: (minAgeSec: number) => Promise<DisplayMinAgeOutcome>,
): Promise<DisplayMinAgeActionResult> {
  const parsed = parseDisplayMinAgeInput(draft);
  if (!parsed.ok) {
    return { status: "error", text: DISPLAY_MIN_AGE_BAD_NUMBER };
  }
  const outcome = await onSave(parsed.value);
  return outcome === "ok"
    ? { status: "ok" }
    : { status: "error", text: failureText(outcome, DISPLAY_MIN_AGE_SAVE_FAILED) };
}

export async function runDisplayMinAgeRemove(
  onRemove: () => Promise<DisplayMinAgeOutcome>,
): Promise<DisplayMinAgeActionResult> {
  const outcome = await onRemove();
  return outcome === "ok"
    ? { status: "ok" }
    : { status: "error", text: failureText(outcome, DISPLAY_MIN_AGE_REMOVE_FAILED) };
}

/**
 * Пока запрос идёт, Escape и щелчок вне окна его не закрывают: иначе
 * «закрыл, а оно сохранилось». Открытие проходит всегда.
 */
function nextOpenState(current: boolean, requested: boolean, busy: boolean): boolean {
  return !requested && busy ? current : requested;
}

interface DisplayMinAgeActions {
  onSave: (minAgeSec: number) => Promise<DisplayMinAgeOutcome>;
  onRemove: () => Promise<DisplayMinAgeOutcome>;
}

interface DisplayMinAgeFormProps extends DisplayMinAgeActions {
  /** Действующая настройка или `null` — не задана. */
  minAgeSec: number | null;
  onClose: () => void;
  /** Запрос начался или закончился: окно не закрывается Escape и щелчком, пока он идёт. */
  onBusyChange: (busy: boolean) => void;
}

/** Поле секунд, «Сохранить» и «Убрать». Блокируются только кнопки, поле и фокус остаются. */
export function DisplayMinAgeForm({
  minAgeSec,
  onSave,
  onRemove,
  onClose,
  onBusyChange,
}: DisplayMinAgeFormProps) {
  const [draft, setDraft] = useState(minAgeSec === null ? "" : String(minAgeSec));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<DisplayMinAgeActionResult>): Promise<void> {
    setBusy(true);
    onBusyChange(true);
    const result = await action();
    setBusy(false);
    onBusyChange(false);
    if (result.status === "ok") {
      setError(null);
      onClose();
      return;
    }
    // Введённое число остаётся в поле: повторить можно тем же нажатием.
    setError(result.text);
  }

  return (
    <form
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        void run(() => runDisplayMinAgeSave(draft, onSave));
      }}
    >
      <Field invalid={error !== null}>
        <FieldLabel>{DISPLAY_MIN_AGE_FIELD_LABEL}</FieldLabel>
        <Input
          inputMode="numeric"
          autoComplete="off"
          placeholder="с"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
        />
        {error !== null ? <FieldError match>{error}</FieldError> : null}
      </Field>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {DISPLAY_MINIMUMS_SAVE}
        </Button>
        {minAgeSec !== null ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void run(() => runDisplayMinAgeRemove(onRemove))}
          >
            {DISPLAY_MINIMUMS_REMOVE}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

interface DisplayMinAgeWindowProps extends DisplayMinAgeActions {
  onReload: () => void;
  state: DisplayMinAgeState;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}

/**
 * Содержимое окна: заголовок и пояснение §82 — всегда; ниже форма, а пока
 * настройка не прочитана — сообщение без полей записи (запись в непрочитанное
 * состояние не ляжет).
 */
export function DisplayMinAgeWindow({
  state,
  onSave,
  onRemove,
  onReload,
  onClose,
  onBusyChange,
}: DisplayMinAgeWindowProps) {
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <PopoverTitle>{DISPLAY_MIN_AGE_TITLE}</PopoverTitle>
        <PopoverDescription className="text-xs">{DISPLAY_MIN_AGE_EXPLANATION}</PopoverDescription>
      </div>
      {state.status === "ready" ? (
        <DisplayMinAgeForm
          // Форма открывается с действующим числом и пересоздаётся при его смене.
          key={state.minAgeSec ?? "none"}
          minAgeSec={state.minAgeSec}
          onSave={onSave}
          onRemove={onRemove}
          onClose={onClose}
          onBusyChange={onBusyChange}
        />
      ) : state.status === "unavailable" ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-warning">
            {DISPLAY_MIN_AGE_UNAVAILABLE_TEXT}
          </p>
          <Button size="sm" variant="outline" onClick={onReload}>
            {DISPLAY_MINIMUMS_RETRY}
          </Button>
        </div>
      ) : (
        <p role="status" className="text-sm text-muted-foreground">
          {DISPLAY_MIN_AGE_LOADING}
        </p>
      )}
    </>
  );
}

interface DisplayMinAgeButtonProps extends DisplayMinAgeActions {
  onReload: () => void;
  state: DisplayMinAgeState;
}

/**
 * Пометка-кнопка полосы фильтров рядом с «Мин. объём». Никогда не `disabled`:
 * из пустого состояния через неё задаётся первое значение. Вид повторяет
 * пометки сводки: акцентная при заданном времени, бледная с пунктиром без
 * него, предупреждающая при ошибке чтения; число и слова — в тексте кнопки.
 */
export function DisplayMinAgeButton({
  state,
  onSave,
  onRemove,
  onReload,
}: DisplayMinAgeButtonProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const unavailable = displayMinAgeUnavailable(state);
  const minAgeSec = state.status === "ready" ? state.minAgeSec : null;
  const tone: ChipTone = unavailable ? "warning" : minAgeSec !== null ? "set" : "unset";

  return (
    <Popover
      open={open}
      onOpenChange={(next) => setOpen((current) => nextOpenState(current, next, busy))}
    >
      <PopoverTrigger
        render={<Button variant={chipVariant(tone)} size="tag" className={chipToneClass(tone)} />}
      >
        <ChipContent
          {...displayMinAgeButtonParts(minAgeSec, unavailable, state.status === "loading")}
        />
      </PopoverTrigger>
      <PopoverContent align="start">
        <DisplayMinAgeWindow
          state={state}
          onSave={onSave}
          onRemove={onRemove}
          onReload={onReload}
          onClose={() => setOpen(false)}
          onBusyChange={setBusy}
        />
      </PopoverContent>
    </Popover>
  );
}
